import dayjs from 'dayjs';
import _ from 'underscore';
import { Box } from '@mui/material';
import { useGet } from 'hooks/use-get';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { clearState, loadState } from 'utils/storage';
import { useAccountState } from 'contexts/account/account-context';

import Paper from 'components/paper';
import Breadcrumbs from 'components/breadcrumbs';
import ProductionSystemsTabHeader from '../../components/tab-header';
import SelectFarm from 'views/production-systems/components/select-farm';
import ListFarms from 'views/production-systems/features/farm-overview/components/list-farms';
import FarmSetupPonds from 'views/production-systems/features/farm-setup/components/farm-setup-ponds';
import SetupActions from 'views/production-systems/features/farm-setup/components/farm-setup-actions';
import FarmSetupFilters from 'views/production-systems/features/farm-setup/components/farm-setup-filters';

import { getPonds } from 'views/production-systems/api/productionSystems';
import { usePondsState } from 'views/production-systems/context/ponds-context';
import { Pond, PondStatus } from 'views/production-systems/features/farm-setup/types/pond.type';
import Filters from 'views/production-systems/features/farm-setup/types/farm-setup-filters.type';

const defaultValues: Filters = {
  zoneId: '',
  searchValue: '',
  registrationDate: dayjs(),
  showActivePonds: true,
  showInactivePonds: true,
  enableKg: true
};

type SortablePond = Pond & {
  status: boolean;
};

const FarmSetupOverview = () => {
  const params = useParams<{ globalSiteId: string }>();
  const { account } = useAccountState();

  const { globalSiteId, registrationSetupDate } = usePondsState();

  //#region STATES
  const [pondList, setPondList] = useState<SortablePond[]>([]);
  const [sourcePonds, setSourcePonds] = useState<SortablePond[]>([]);
  const [filters, setFilters] = useState<Filters>(loadState({ key: 'farmSetupFilters' }) || defaultValues);
  //#endregion

  const { data: pondState, isLoading: loadingPonds } = useGet(
    ['getPonds', globalSiteId, registrationSetupDate.format('YYYY-MM-DD')],
    ({ queryKey }) => getPonds(String(queryKey[1]), String(queryKey[2])),
    {
      enabled: !!globalSiteId,
    }
  );

  useEffect(() => {
    if (!pondState) return;

    let ponds: SortablePond[] = _.chain(pondState.ponds)
      .map((pond) => ({
        ...pond,
        status: pond.pondStatus === PondStatus.active ? true : false,
      }))
      .sortBy('pondName')
      .value();

    setSourcePonds(ponds);
  }, [pondState]);

  useEffect(() => {
    const rows = _.chain(sourcePonds)
      .filter((pond) => (filters.showInactivePonds ? true : pond.pondStatus === PondStatus.active))
      .filter((pond) => (filters.showActivePonds ? true : pond.pondStatus === PondStatus.inactive))
      .filter((pond) => (!filters.zoneId ? true : pond.zoneId === filters.zoneId))
      .filter((pond) => pond.pondName.toLowerCase().includes(filters.searchValue.toLowerCase()))
      .value();
    setPondList(rows);
  }, [filters, sourcePonds]);

  return (
    <>
      <ProductionSystemsTabHeader
        utils={
          <Breadcrumbs
            crumbs={
              !!params.globalSiteId
                ? [
                  {
                    url: params.globalSiteId,
                    text: account.company?.addressAccess.find((a) => a.id === params.globalSiteId)?.name || '',
                  },
                ]
                : undefined
            }
          />
        }
      />
      <Box display={'flex'} justifyContent={'flex-end'} pb={4}>
        <SelectFarm
          selectedId={!!params.globalSiteId ? params.globalSiteId : ''}
          onChange={() => clearState({ key: 'pondFilters' })}
          uri="/production-systems/farm-setup"
        />
      </Box>
      <Paper transparent>
        <Box display={'flex'} flexDirection={'column'} gap={4}>
          <FarmSetupFilters renderPonds={pondList} filters={filters} setFilters={setFilters} />
          <FarmSetupPonds ponds={pondList} setPonds={setPondList} isFetching={loadingPonds} isKg={filters.enableKg} />
          {!loadingPonds && <SetupActions ponds={pondList} />}
        </Box>
      </Paper>
    </>
  );
};

const FarmSetup = () => {
  const params = useParams<{ globalSiteId: string }>();

  return !!params.globalSiteId ? <FarmSetupOverview /> : <ListFarms uri={'/production-systems/farm-setup'} />;
};

export default FarmSetup;
